import { parseISO, startOfDay } from "date-fns";
import { groupBy, sortBy } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { ChatReplyDto } from "@/api/types";
import { ChatMessage, type ChatMessageModel } from "@/components/ChatWindow/ChatMessage";
import { SystemChatMessage } from "@/components/ChatWindow/SystemChatMessage";
import { CommentField } from "@/components/CommentField/CommentField";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { InfoCircleIcon } from "@/components/Icons/InfoCircleIcon";
import { SlashCircle01Icon } from "@/components/Icons/SlashCircle01Icon";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { isImageUploaded, useImageInput } from "@/components/ImageInput/useImageInput";
import { type FormVideo, isVideoUploaded, useVideoInput } from "@/components/VideoInput/useVideoInput";
import { Badge } from "@/design-system/Badge";
import { Banner } from "@/design-system/Banner";
import { Card } from "@/design-system/Card";
import { Heading } from "@/design-system/Heading";
import { Inline } from "@/design-system/Inline";
import { ModalBodySpinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useTranslation } from "@/translations";

import { useChatScrollBehavior } from "./useChatScrollBehavior";

export type ChatReplyPayload = {
  message: string;
  images: FormImage[];
  videos: FormVideo[];
};

type ChatWindowProps = {
  chatReplies: ChatReplyDto[];
  canSendChatReply: boolean;
  hasMoreReplies: boolean | undefined;
  isLoadingMoreReplies: boolean;
  fetchMoreReplies: () => void;
  sendReply: (payload: ChatReplyPayload) => Promise<ChatReplyDto>;
  statuses?: {
    isBlockedBySender?: boolean;
    isBlockedByReceiver?: boolean;
    isDisabledBySender?: boolean;
    isDisabledByReceiver?: boolean;
  };
  prefilledReply?: string;
} & (
  | {
      chatName: string;
      renderChatName?: (chatName: string) => React.ReactNode;
    }
  | {
      chatName?: never;
      renderChatName?: never;
    }
);

interface ChatWindowFormValues {
  replyValue: string;
}

type OptimisticChatReply = ChatMessageModel & {
  optimisticId: string;
  payload?: ChatReplyPayload;
};

const groupChatRepliesByDate = <T extends { sentAt: string }>(chatMessages: T[]): [string, T[]][] => {
  const sortedChatReplies = sortBy(chatMessages, (x) => parseISO(x.sentAt).valueOf());
  const groupedByDay = Object.entries(groupBy(sortedChatReplies, (x) => startOfDay(x.sentAt).valueOf()));

  return sortBy(groupedByDay, ([date]) => Number(date));
};

export function ChatWindow({
  chatReplies,
  chatName,
  renderChatName,
  canSendChatReply,
  hasMoreReplies,
  isLoadingMoreReplies,
  statuses,
  sendReply,
  fetchMoreReplies,
  prefilledReply,
}: ChatWindowProps): React.ReactNode {
  const [images, setImages] = useState<FormImage[]>([]);
  const [videos, setVideos] = useState<FormVideo[]>([]);
  const [optimisticReplies, setOptimisticReplies] = useState<OptimisticChatReply[]>([]);
  const serverReplyIds = useMemo(() => new Set(chatReplies.map((reply) => reply.id)), [chatReplies]);
  const optimisticReplyCount = optimisticReplies.filter((reply) => !serverReplyIds.has(reply.id)).length;

  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const { addImages, removeImage } = useImageInput({ selectedImages: images, onChange: setImages });
  const refReplyInputField = useRef<HTMLTextAreaElement>(null);
  const isMountedRef = useRef(true);
  const optimisticRepliesRef = useRef<OptimisticChatReply[]>([]);

  const { addVideos, removeVideo } = useVideoInput({
    selectedVideos: videos,
    onChange: setVideos,
  });
  const form = useForm<ChatWindowFormValues>({
    defaultValues: {
      replyValue: prefilledReply ? `\n${prefilledReply}` : "",
    },
  });
  const {
    captureAnchorBeforePrepend,
    onScroll: handleScrollMessages,
    requestScrollToBottomAfterNextReply,
    scrollRef,
  } = useChatScrollBehavior({ replyCount: chatReplies.length + optimisticReplyCount });

  const updateOptimisticReplies = useCallback(
    (updater: (currentReplies: OptimisticChatReply[]) => OptimisticChatReply[]) => {
      setOptimisticReplies((currentReplies) => {
        const updatedReplies = updater(currentReplies);
        optimisticRepliesRef.current = updatedReplies;

        return updatedReplies;
      });
    },
    [],
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      optimisticRepliesRef.current.forEach((reply) => reply.payload && cleanupChatReplyPayload(reply.payload));
    };
  }, []);

  useEffect(() => {
    updateOptimisticReplies((currentReplies) => currentReplies.filter((reply) => !serverReplyIds.has(reply.id)));
  }, [serverReplyIds, updateOptimisticReplies]);

  const handleFetchMoreReplies = useCallback(() => {
    if (captureAnchorBeforePrepend()) fetchMoreReplies();
  }, [captureAnchorBeforePrepend, fetchMoreReplies]);

  const loaderRef = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreReplies && hasMoreReplies) {
        handleFetchMoreReplies();
      }
    }, [handleFetchMoreReplies, hasMoreReplies, isLoadingMoreReplies]),
  });

  useEffect(() => {
    if (!prefilledReply || !refReplyInputField.current) {
      return;
    }

    form.setValue("replyValue", `\n${prefilledReply}`);

    // Autofocus and place user's caret at the start of the input field
    refReplyInputField.current.selectionStart = 0;
    refReplyInputField.current.selectionEnd = 0;

    refReplyInputField.current.focus();
  }, [form, prefilledReply]);

  useEffect(() => {
    form.register("replyValue");
  }, [form]);

  const currReplyValue = useWatch({
    name: "replyValue",
    control: form.control,
  });
  const handleChangeReply = useCallback((value: string) => form.setValue("replyValue", value), [form]);

  const sendOptimisticReply = useCallback(
    async (optimisticId: string, payload: ChatReplyPayload) => {
      try {
        const savedReply = await sendReply(payload);
        cleanupChatReplyPayload(payload);

        if (!isMountedRef.current) {
          return;
        }

        updateOptimisticReplies((currentReplies) =>
          currentReplies.map((reply) =>
            reply.optimisticId === optimisticId ? { ...savedReply, optimisticId } : reply,
          ),
        );
      } catch {
        const retryPayload = cloneChatReplyPayloadForRetry(payload);
        cleanupChatReplyPayload(payload);

        if (!isMountedRef.current) {
          cleanupChatReplyPayload(retryPayload);

          return;
        }

        updateOptimisticReplies((currentReplies) =>
          currentReplies.map((reply) =>
            reply.optimisticId === optimisticId
              ? {
                  ...reply,
                  image: retryPayload.images[0],
                  videos: retryPayload.videos,
                  deliveryStatus: "failed",
                  payload: retryPayload,
                }
              : reply,
          ),
        );
      }
    },
    [sendReply, updateOptimisticReplies],
  );

  const handleRetryReply = useCallback(
    (optimisticId: string) => {
      const reply = optimisticRepliesRef.current.find((candidate) => candidate.optimisticId === optimisticId);
      if (!reply?.payload || reply.deliveryStatus === "sending") {
        return;
      }

      updateOptimisticReplies((currentReplies) =>
        currentReplies.map((candidate) =>
          candidate.optimisticId === optimisticId ? { ...candidate, deliveryStatus: "sending" } : candidate,
        ),
      );
      void sendOptimisticReply(optimisticId, reply.payload);
    },
    [sendOptimisticReply, updateOptimisticReplies],
  );

  const displayedReplies = useMemo<ChatMessageModel[]>(() => {
    const localReplies = optimisticReplies
      .filter((reply) => !serverReplyIds.has(reply.id))
      .map((reply) => ({
        ...reply,
        onRetry: reply.deliveryStatus === "failed" ? () => handleRetryReply(reply.optimisticId) : undefined,
      }));

    return [...chatReplies, ...localReplies];
  }, [chatReplies, handleRetryReply, optimisticReplies, serverReplyIds]);
  const chatMessagesByDate = useMemo(() => groupChatRepliesByDate(displayedReplies), [displayedReplies]);

  const handleSendReply = () => {
    const payload: ChatReplyPayload = {
      message: form.getValues("replyValue"),
      images,
      videos,
    };
    if (!payload.message.trim() && payload.images.length === 0 && payload.videos.length === 0) {
      return Promise.resolve();
    }

    const optimisticId = crypto.randomUUID();

    requestScrollToBottomAfterNextReply();
    updateOptimisticReplies((currentReplies) => [
      ...currentReplies,
      {
        optimisticId,
        id: `optimistic-${optimisticId}`,
        text: payload.message.trim(),
        sentAt: new Date().toISOString(),
        author: sessionUser,
        image: payload.images[0],
        videos: payload.videos,
        isUnread: false,
        hasBeenSeenByAll: false,
        deliveryStatus: "sending",
        payload,
      },
    ]);

    setImages([]);
    setVideos([]);
    form.setValue("replyValue", "");
    refReplyInputField.current?.focus();
    void sendOptimisticReply(optimisticId, payload);

    return Promise.resolve();
  };

  const handleRemoveImage = useCallback(
    (imageToBeRemoved: FormImage) => {
      removeImage(imageToBeRemoved);
    },
    [removeImage],
  );

  const bannerType:
    | "blockedBySender"
    | "blockedByReceiver"
    | "receiverChatDisabled"
    | "autoTranslationEnabled"
    | "senderChatDisabled"
    | undefined = useMemo(() => {
    if (statuses?.isBlockedBySender) {
      return "blockedBySender";
    } else if (statuses?.isBlockedByReceiver) {
      return "blockedByReceiver";
    } else if (statuses?.isDisabledByReceiver) {
      return "receiverChatDisabled";
    } else if (statuses?.isDisabledBySender) {
      return "senderChatDisabled";
    } else if (sessionUser.autoTranslationLanguage) {
      return "autoTranslationEnabled";
    }

    return undefined;
  }, [
    sessionUser.autoTranslationLanguage,
    statuses?.isBlockedByReceiver,
    statuses?.isBlockedBySender,
    statuses?.isDisabledByReceiver,
    statuses?.isDisabledBySender,
  ]);

  return (
    <Card as="section" density="flush" height="full" clip>
      {chatName && (
        <header className="flex w-full border-b border-border-default bg-background-default p-space-md">
          {renderChatName ? renderChatName(chatName) : <Heading level={2}>{chatName}</Heading>}
        </header>
      )}
      <aside className="h-fit">
        {/* Block banner takes precedence */}
        {bannerType === "blockedBySender" && (
          <Banner
            icon={<SlashCircle01Icon />}
            data-testid="blocked-user-private-chat-banner"
            message={t("component.blocked-chat-banner.blocked-by-self")}
            variant="danger"
          />
        )}
        {bannerType === "blockedByReceiver" && (
          <Banner
            icon={<SlashCircle01Icon />}
            data-testid="blocked-by-other-private-chat-banner"
            message={t("component.blocked-chat-banner.blocked-by-other")}
            variant="danger"
          />
        )}
        {bannerType === "receiverChatDisabled" && (
          <Banner
            icon={<InfoCircleIcon />}
            message={t("component.blocked-chat-banner.receiver-disabled-chat")}
            variant="warning"
          />
        )}
        {bannerType === "senderChatDisabled" && (
          <Banner
            icon={<InfoCircleIcon />}
            message={t("component.blocked-chat-banner.sender-disabled-chat")}
            variant="warning"
          />
        )}
        {bannerType === "autoTranslationEnabled" && (
          <Banner icon={<Translate01Icon />} message={t("common.label.this-content-is-auto-translated")} />
        )}
      </aside>
      <div
        className="flex max-h-full flex-1 flex-col gap-space-md overflow-y-auto px-space-lg pt-space-lg pb-space-md [overflow-anchor:none]"
        onScroll={handleScrollMessages}
        ref={scrollRef}
      >
        {hasMoreReplies && (
          <section ref={loaderRef}>
            <ModalBodySpinner />
          </section>
        )}
        {chatMessagesByDate.map(([date, chatMessages]) => (
          <section key={date.toString()} data-testid="chat-message-date-group">
            <header className="mb-space-md flex items-center justify-center whitespace-nowrap">
              <Badge variant="default">
                <FormattedDate format="date" date={new Date(Number(date)).toISOString()} />
              </Badge>
            </header>
            <Stack gap="sm">
              {chatMessages.map((chatMessage, index) =>
                chatMessage.replyType?.includes("system") ? (
                  <Inline key={chatMessage.id} align="center" justify="center" wrap={false}>
                    <SystemChatMessage
                      chatName={chatName}
                      chatMessage={chatMessage as ChatReplyDto}
                      presentation="bubble"
                    />
                  </Inline>
                ) : (
                  <ChatMessage
                    key={chatMessage.id}
                    isLastOfMessageGroup={chatMessages[index + 1]?.author?.id === chatMessage.author?.id}
                    message={chatMessage}
                  />
                ),
              )}
            </Stack>
          </section>
        ))}
      </div>
      <form className="rounded-b-lg bg-background-on-default p-space-md">
        <CommentField
          user={sessionUser}
          value={currReplyValue}
          placeholder={t("page.chat-detail.chat.input.placeholder")}
          onChange={handleChangeReply}
          onSubmit={handleSendReply}
          allowedAttachments={["image", "video"]}
          images={images}
          onAddImages={addImages}
          onRemoveImage={handleRemoveImage}
          videos={videos}
          onAddVideo={addVideos}
          onRemoveVideo={removeVideo}
          tooltips={{
            send: t("page.chat-detail.chat.input-tooltip"),
          }}
          submissionMode="optimistic"
          disabled={!canSendChatReply}
          ref={refReplyInputField}
        />
      </form>
    </Card>
  );
}

function cleanupChatReplyPayload(payload: ChatReplyPayload): void {
  payload.images.forEach((image) => {
    if (!isImageUploaded(image)) {
      URL.revokeObjectURL(image.url);
    }
  });
  payload.videos.forEach((video) => {
    if (!isVideoUploaded(video)) {
      video.abortController?.abort();
      URL.revokeObjectURL(video.url);
    }
  });
}

function cloneChatReplyPayloadForRetry(payload: ChatReplyPayload): ChatReplyPayload {
  return {
    message: payload.message,
    images: payload.images.map((image) =>
      isImageUploaded(image)
        ? image
        : {
            file: image.file,
            url: URL.createObjectURL(image.file),
          },
    ),
    videos: payload.videos.map((video) =>
      isVideoUploaded(video)
        ? video
        : {
            file: video.file,
            url: URL.createObjectURL(video.file),
            abortController: new AbortController(),
          },
    ),
  };
}
